import React from 'react';
import { Clock, Globe, CheckCircle, XCircle, ExternalLink, Info } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Store, StoreCategory } from '../../types';

interface StoreCardProps {
  store: Store;
  onViewDetails: (store: Store) => void;
  onToggleStatus?: (storeId: string) => void;
}

export const StoreCard: React.FC<StoreCardProps> = ({
  store,
  onViewDetails,
  onToggleStatus
}) => {
  const getCategoryColor = (category: StoreCategory) => {
    switch (category) {
      case 'streaming': return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'download': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
      case 'social': return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
      case 'radio': return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
      case 'gaming': return 'text-red-400 bg-red-500/10 border-red-500/20';
      default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  const getCategoryLabel = (category: StoreCategory) => {
    switch (category) {
      case 'streaming': return 'Streaming';
      case 'download': return 'Download';
      case 'social': return 'Social Media';
      case 'radio': return 'Radio';
      case 'gaming': return 'Gaming';
      default: return 'Other';
    }
  };

  return (
    <Card className="relative group" hover>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-3xl">{store.logo}</div>
          <div>
            <h3 className="text-lg font-semibold text-white font-display">
              {store.name}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(store.category)}`}>
                {getCategoryLabel(store.category)}
              </span>
              {store.isActive ? (
                <div className="flex items-center space-x-1 text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  <span className="text-xs">Active</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-red-400">
                  <XCircle className="w-3 h-3" />
                  <span className="text-xs">Inactive</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => onViewDetails(store)}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
        >
          <Info className="w-4 h-4" />
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-400 mb-4 line-clamp-2">
        {store.description}
      </p>

      {/* Details */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-gray-400">
            <Clock className="w-4 h-4" />
            <span>Processing Time</span>
          </div>
          <span className="text-white font-medium">{store.processingTime}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-gray-400">
            <Globe className="w-4 h-4" />
            <span>Regions</span>
          </div>
          <span className="text-white font-medium">
            {store.region.length > 2 
              ? `${store.region.slice(0, 2).join(', ')} +${store.region.length - 2}`
              : store.region.join(', ')
            }
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-700">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(store)}
          className="flex-1"
          icon={ExternalLink}
        >
          View Details
        </Button>
        {onToggleStatus && (
          <Button
            variant={store.isActive ? "danger" : "primary"}
            size="sm"
            onClick={() => onToggleStatus(store.id)}
            className="flex-1"
          >
            {store.isActive ? 'Disable' : 'Enable'}
          </Button>
        )}
      </div>

      {/* Status Indicator */}
      <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
        store.isActive ? 'bg-green-400' : 'bg-red-400'
      }`} />
    </Card>
  );
};